import React, { useState } from 'react'
import { withTranslation } from 'react-i18next'
import {
  Box,
  Alert,
  Typography,
  TextField,
  Button,
  Paper,
  Link
} from '@mui/material'
import { useMutation, gql } from '@apollo/client'
import { forgotVendorPassword, verifyVendorOtp, resetVendorPassword } from '../apollo'
import Logo from '../assets/img/rushmyfood-logo.png'

const FORGOT_VENDOR_PASSWORD = gql`
  ${forgotVendorPassword}
`
const VERIFY_VENDOR_OTP = gql`
  ${verifyVendorOtp}
`
const RESET_VENDOR_PASSWORD = gql`
  ${resetVendorPassword}
`

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    backgroundColor: 'rgba(102, 126, 234, 0.1)',
    '& fieldset': { borderColor: 'transparent' },
    '&:hover fieldset': { borderColor: '#667eea' },
    '&.Mui-focused fieldset': { borderColor: '#667eea' }
  },
  '& .MuiOutlinedInput-input': { color: '#1a1a2e' },
  '& .MuiInputBase-input::placeholder': { color: '#888', opacity: 1 }
}

const buttonSx = {
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: '#fff',
  padding: '14px',
  borderRadius: '10px',
  fontWeight: 600,
  fontSize: '1rem',
  textTransform: 'none',
  boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
  '&:hover': {
    background: 'linear-gradient(135deg, #5a6fd6 0%, #6a4190 100%)',
    boxShadow: '0 6px 20px rgba(102, 126, 234, 0.5)'
  }
}

const ForgotPassword = props => {
  const [step, setStep] = useState(1) // 1=email, 2=otp, 3=new password
  const [email, setEmail] = useState('')
  const [otp, setOtp] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [loading, setLoading] = useState(false)

  const [mutateForgot] = useMutation(FORGOT_VENDOR_PASSWORD)
  const [mutateVerify] = useMutation(VERIFY_VENDOR_OTP)
  const [mutateReset] = useMutation(RESET_VENDOR_PASSWORD)

  const handleSendOtp = async() => {
    if (!email.trim()) {
      setError('Please enter your email address')
      return
    }
    setError(null)
    setLoading(true)
    try {
      await mutateForgot({ variables: { email: email.trim().toLowerCase(), otp: '' } })
      setStep(2)
    } catch (err) {
      const msg =
        err.graphQLErrors?.[0]?.message ||
        err.networkError?.result?.errors?.[0]?.message ||
        err.message ||
        'Failed to send OTP'
      setError(msg)
    } finally {
      setLoading(false)
    }
  }

  const handleVerifyOtp = async () => {
    if (!otp.trim()) {
      setError('Please enter the OTP')
      return
    }
    setError(null)
    setLoading(true)
    try {
      // Server validates the code; the browser never knows the correct value.
      await mutateVerify({ variables: { email: email.trim().toLowerCase(), code: otp.trim() } })
      setStep(3)
    } catch (err) {
      const msg =
        err.graphQLErrors?.[0]?.message ||
        err.networkError?.result?.errors?.[0]?.message ||
        err.message ||
        'Incorrect OTP. Please try again.'
      setError(msg)
    } finally {
      setLoading(false)
    }
  }

  const handleResetPassword = async() => {
    if (!password) {
      setError('Please enter a new password')
      return
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }
    setError(null)
    setLoading(true)
    try {
      // No code here — it was already verified in step 2. Server checks the
      // recent-verification window instead (avoids double-consuming the OTP).
      await mutateReset({ variables: { email: email.trim().toLowerCase(), password } })
      setSuccess('Password reset successfully! You can now log in.')
    } catch (err) {
      const msg =
        err.graphQLErrors?.[0]?.message ||
        err.networkError?.result?.errors?.[0]?.message ||
        err.message ||
        'Failed to reset password'
      setError(msg)
    } finally {
      setLoading(false)
    }
  }

  const stepTitle = step === 1 ? 'Forgot Password' : step === 2 ? 'Enter OTP' : 'Set New Password'
  const stepSubtitle =
    step === 1
      ? 'Enter your email to receive a one-time password'
      : step === 2
      ? `We sent a 6-digit OTP to ${email}`
      : 'Create a new password for your account'

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: 2
      }}
    >
      <Paper
        elevation={24}
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          borderRadius: '20px',
          overflow: 'hidden',
          maxWidth: 900,
          width: '100%',
          minHeight: { xs: 'auto', md: 500 }
        }}
      >
        {/* Left Side - Branding */}
        <Box
          sx={{
            flex: 1,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: { xs: 3, md: 4 },
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0, left: 0, right: 0, bottom: 0,
              backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px)',
              backgroundSize: '20px 20px'
            }
          }}
        >
          <Paper
            elevation={8}
            sx={{
              padding: 4,
              borderRadius: '16px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mb: 4,
              backgroundColor: '#fff'
            }}
          >
            <img
              src={Logo}
              alt="RushmyFood Logo"
              style={{ width: 120, height: 'auto', marginBottom: 8 }}
            />
          </Paper>

          <Typography
            variant="h4"
            sx={{
              color: '#fff',
              fontWeight: 700,
              textAlign: 'center',
              mb: 2,
              position: 'relative',
              zIndex: 1
            }}
          >
            Reset Your Password
          </Typography>
          <Typography
            sx={{
              color: 'rgba(255,255,255,0.8)',
              textAlign: 'center',
              maxWidth: 280,
              position: 'relative',
              zIndex: 1
            }}
          >
            Follow the steps to securely reset your account password
          </Typography>

          {/* Step indicators */}
          <Box sx={{ display: 'flex', gap: 1, mt: 4, position: 'relative', zIndex: 1 }}>
            {[1, 2, 3].map(s => (
              <Box
                key={s}
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: s <= step ? '#fff' : 'rgba(255,255,255,0.3)',
                  color: s <= step ? '#764ba2' : '#fff',
                  fontWeight: 700,
                  fontSize: '0.9rem'
                }}
              >
                {s}
              </Box>
            ))}
          </Box>
        </Box>

        {/* Right Side - Form */}
        <Box
          sx={{
            flex: 1,
            padding: { xs: 3, md: 5 },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            backgroundColor: '#fff'
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a2e', mb: 1 }}>
            {stepTitle}
          </Typography>
          <Typography sx={{ color: '#666', mb: 4 }}>
            {stepSubtitle}
          </Typography>

          {/* Step 1: Email */}
          {step === 1 && (
            <Box>
              <Typography sx={{ fontWeight: 600, mb: 1, color: '#1a1a2e' }}>
                Email Address
              </Typography>
              <TextField
                fullWidth
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="Enter your email"
                variant="outlined"
                onKeyPress={e => e.key === 'Enter' && handleSendOtp()}
                sx={{ ...inputSx, mb: 3 }}
              />
              <Button
                fullWidth
                disabled={loading}
                onClick={handleSendOtp}
                sx={buttonSx}
              >
                {loading ? 'Sending OTP...' : 'Send OTP'}
              </Button>
            </Box>
          )}

          {/* Step 2: OTP */}
          {step === 2 && (
            <Box>
              <Typography sx={{ fontWeight: 600, mb: 1, color: '#1a1a2e' }}>
                One-Time Password
              </Typography>
              <TextField
                fullWidth
                value={otp}
                onChange={e => setOtp(e.target.value)}
                placeholder="Enter 6-digit OTP"
                variant="outlined"
                inputProps={{ maxLength: 6 }}
                onKeyPress={e => e.key === 'Enter' && handleVerifyOtp()}
                sx={{ ...inputSx, mb: 3 }}
              />
              <Button fullWidth onClick={handleVerifyOtp} sx={{ ...buttonSx, mb: 2 }}>
                Verify OTP
              </Button>
              <Button
                fullWidth
                variant="text"
                disabled={loading}
                onClick={() => {
                  setError(null)
                  handleSendOtp()
                }}
                sx={{ color: '#667eea', textTransform: 'none' }}
              >
                {loading ? 'Resending...' : 'Resend OTP'}
              </Button>
            </Box>
          )}

          {/* Step 3: New Password */}
          {step === 3 && !success && (
            <Box>
              <Typography sx={{ fontWeight: 600, mb: 1, color: '#1a1a2e' }}>
                New Password
              </Typography>
              <TextField
                fullWidth
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Enter new password"
                variant="outlined"
                sx={{ ...inputSx, mb: 3 }}
              />
              <Typography sx={{ fontWeight: 600, mb: 1, color: '#1a1a2e' }}>
                Confirm Password
              </Typography>
              <TextField
                fullWidth
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                variant="outlined"
                onKeyPress={e => e.key === 'Enter' && handleResetPassword()}
                sx={{ ...inputSx, mb: 3 }}
              />
              <Button
                fullWidth
                disabled={loading}
                onClick={handleResetPassword}
                sx={buttonSx}
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2, borderRadius: '10px' }}>
              {error}
            </Alert>
          )}
          {success && (
            <Box>
              <Alert severity="success" sx={{ mt: 2, borderRadius: '10px' }}>
                {success}
              </Alert>
              <Link
                href="/#/auth/login"
                sx={{
                  display: 'block',
                  mt: 2,
                  textAlign: 'center',
                  color: '#667eea',
                  fontWeight: 600,
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                Back to Login
              </Link>
            </Box>
          )}

          {!success && (
            <Link
              href="/#/auth/login"
              sx={{
                display: 'block',
                mt: 3,
                textAlign: 'center',
                color: '#667eea',
                textDecoration: 'none',
                fontSize: '0.9rem',
                '&:hover': { textDecoration: 'underline' }
              }}
            >
              Back to Login
            </Link>
          )}
        </Box>
      </Paper>
    </Box>
  )
}

export default withTranslation()(ForgotPassword)
